import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";

// Lazy-loaded components
const VlogAdminList = React.lazy(() => import("./VlogAdminList"));
const VlogUpload = React.lazy(() => import("./VlogUpload"));
const VlogEditor = React.lazy(() => import("./VlogEditor"));
const VlogCategories = React.lazy(() => import("./VlogCategories"));
const VlogSeriesAdmin = React.lazy(() => import("./VlogSeriesAdmin"));
const VlogNotificationSettings = React.lazy(() => import("./components/VlogNotificationSettings"));

// Public (no-auth) pages
const VlogCatalogue = React.lazy(() => import("./VlogCatalogue"));
const VlogSeriesPage = React.lazy(() => import("./VlogSeriesPage"));
const VlogPostView = React.lazy(() => import("./VlogPostView"));

const vlogModuleConfig: ModuleConfig = {
  // MODULE IDENTITY
  id: "vlog",
  name: "Vlog",
  description: "Shoot a video, drag it on, and it's a live public page — with inline-editable headline/bullets/CTA, skins, stats, and email notification to the uploader",
  version: "1.1.0",
  enabled: true,
  category: "content",
  order: 11,
  icon: "🎬",
  color: "#2563EB",

  // ROUTES
  routes: [
    // --- Admin/Author protected routes ---
    {
      path: "/vlog/admin",
      component: VlogAdminList,
      permissionLevel: "view",
      useLayout: true,
    },
    {
      path: "/vlog/upload",
      component: VlogUpload,
      permissionLevel: "create",
      useLayout: true,
      layoutOptions: { maxW: "container.md", py: 8 },
    },
    {
      path: "/vlog/admin/new",
      component: VlogEditor,
      permissionLevel: "create",
      useLayout: true,
      layoutOptions: { maxW: "container.xl", py: 4 },
    },
    {
      path: "/vlog/admin/:id/edit",
      component: VlogEditor,
      permissionLevel: "edit",
      useLayout: true,
      layoutOptions: { maxW: "container.xl", py: 4 },
    },
    {
      path: "/vlog/admin/series",
      component: VlogSeriesAdmin,
      permissionLevel: "edit",
      useLayout: true,
      layoutOptions: { maxW: "container.lg", py: 6 },
    },
    {
      path: "/vlog/admin/categories",
      component: VlogCategories,
      permissionLevel: "edit",
      useLayout: true,
      layoutOptions: { maxW: "container.lg", py: 6 },
    },
    {
      path: "/vlog/admin/notifications",
      component: VlogNotificationSettings,
      permissionLevel: "edit",
      useLayout: true,
    },
    // --- Public reader routes (no auth, seamless page auth pattern) ---
    {
      path: "/watch",
      component: VlogCatalogue,
      permissions: [],
      public: true,
    },
    {
      path: "/watch/series/:slug",
      component: VlogSeriesPage,
      permissions: [],
      public: true,
    },
    {
      path: "/watch/:slug",
      component: VlogPostView,
      permissions: [],
      public: true,
    },
  ],

  // NAVIGATION
  navigation: [
    { label: "Upload Video", path: "/vlog/upload", icon: "📤", permissionLevel: "create" },
    { label: "All Vlogs", path: "/vlog/admin", icon: "🎬", permissionLevel: "view" },
    { label: "New Vlog", path: "/vlog/admin/new", icon: "🎥", permissionLevel: "create" },
    { label: "Series", path: "/vlog/admin/series", icon: "📚", permissionLevel: "edit" },
    { label: "Categories", path: "/vlog/admin/categories", icon: "📂", permissionLevel: "edit" },
    { label: "Notifications", path: "/vlog/admin/notifications", icon: "🔔", permissionLevel: "edit" },
    { label: "Public Catalogue", path: "/watch", icon: "🌐", permissions: [] },
  ],

  // PERMISSIONS — resolved from backend SSOT at runtime
  permissions: {
    view: [],
    create: [],
    edit: [],
    delete: [],
  },

  // QUICK ACTIONS
  quickActions: [
    { label: "Upload Video", path: "/vlog/upload", icon: "📤", description: "Drag a video on — live public page in seconds" },
    { label: "New Vlog", path: "/vlog/admin/new", icon: "🎥", description: "Create a vlog by hand" },
    { label: "Watch", path: "/watch", icon: "🌐", description: "View the public catalogue" },
  ],

  // CHANGELOG
  changelog: [
    {
      version: "1.1.0",
      releaseDate: "2026-08-10",
      title: "Upload-to-live-page + inline editing",
      features: [
        "✅ Drag-and-drop / mobile video upload (chunked mediaCDN* → IPFS) — instant public page on completion",
        "✅ Uploader email on publish (createVlogPost-as-PUBLISHED now fires notification rules)",
        "✅ Inline editing on the public page: headline, description, bullet points, CTA (admins only)",
        "✅ Editable action button with click tracking (trackVlogCtaClick, public + rate-limited)",
        "✅ Admin stats strip: views, reactions, button clicks + skin switcher",
        "✅ Page skins: Clean / Bold / Warm — stored on the post, visible to anonymous visitors",
        "✅ IPFS compliance: thumbnailCid, public gateway query (publicTenantIpfsGatewayUrl), module-local utils/ipfs.ts",
        "✅ Security: videoCustomIframe sanitized server-side (allowlisted https embed hosts, canonical rebuild) — no more raw HTML injection",
        "✅ seriesSlug/seriesName FieldResolvers — series strip on episode pages works",
        "✅ Brand-neutral: tenant name/colors from brandConfig + skins (LEC hardcodes removed)",
        "✅ Generated GraphQL types adopted across the module",
      ],
      notes: "The 'shoot → drag → live page → email' loop. Public /watch ops whitelisted in PUBLIC_OPERATIONS (anonymous viewing was broken).",
    },
    {
      version: "1.0.0",
      releaseDate: "2026-04-13",
      title: "Initial Release",
      features: [
        "✅ Public /watch catalogue with series rows and hero",
        "✅ Series grouping (prev/next episode navigation)",
        "✅ Collapsible transcript (SEO-indexed in DOM)",
        "✅ Companion article markdown below the player",
        "✅ Reactions, polls, AI chat, social proof, progress unlock",
        "✅ Provider-aware player (YouTube/Vimeo/Rumble/Bitchute/IPFS/custom iframe)",
        "✅ Seamless public auth — no login wall on /watch routes",
        "✅ Tom AI content-mode: create/update/publish vlogs via chat with preview→confirm",
      ],
      notes: "Initial vlog module. Mirrors blog module architecture with video-first UX and series support.",
    },
  ],
};

export default vlogModuleConfig;
